import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  PauseCircle as PauseIcon,
  PlayCircle as ResumeIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Report as ReportIcon,
} from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', color: 'warning' },
  { key: 'approved', label: 'Live / Approved', color: 'success' },
  { key: 'rejected', label: 'Not Approved', color: 'default' },
  { key: 'blocked', label: 'Blocked', color: 'error' },
  { key: 'all', label: 'All', color: 'primary' },
];

const statusChip = (status, isPaused) => {
  if (isPaused && status === 'approved') {
    return <Chip size="small" label="Paused (hidden)" color="warning" />;
  }
  const map = {
    pending: { label: 'Pending Review', color: 'warning' },
    approved: { label: 'Live', color: 'success' },
    rejected: { label: 'Not Approved', color: 'default' },
    blocked: { label: 'Blocked', color: 'error' },
    draft: { label: 'Draft', color: 'info' },
  };
  const cfg = map[status] || { label: status, color: 'default' };
  return <Chip size="small" label={cfg.label} color={cfg.color} />;
};

/**
 * Citizen Listings moderation (OLX-style):
 * approve / reject / block + pause/resume without losing approval.
 */
const UserPostings = () => {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [view, setView] = useState('listings'); // listings | reports

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (tab !== 'all') params.status = tab;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API_BASE_URL}/citizen-postings/admin/postings`, {
        params,
        headers: authHeaders(),
      });
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load postings');
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  const loadReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/citizen-postings/admin/reports`, {
        params: { status: 'open' },
        headers: authHeaders(),
      });
      if (res.data?.success) {
        setReports(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch {
      // Reports table may not exist until first report / migration — ignore.
      setReports([]);
    }
  }, []);

  useEffect(() => {
    loadRows();
    loadReports();
  }, [loadRows, loadReports]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, blocked: 0 };
    rows.forEach((r) => {
      if (c[r.status] !== undefined) c[r.status]++;
    });
    return c;
  }, [rows]);

  const review = async (action) => {
    if (!selected) return;
    setActing(true);
    setError('');
    try {
      await axios.patch(
        `${API_BASE_URL}/citizen-postings/admin/postings/${selected.id}/review`,
        { action, admin_notes: notes },
        { headers: authHeaders() },
      );
      setInfo(
        action === 'pause'
          ? 'Listing paused — hidden from public feed (approval kept).'
          : action === 'resume'
            ? 'Listing resumed — visible again.'
            : `Action “${action}” applied.`,
      );
      setSelected(null);
      setNotes('');
      loadRows();
      loadReports();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Citizen Listings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              OLX-style marketplace moderation. Second-hand / rentals / real estate are allowed only
              when lawful. Pending → manual approve. Pause hides without re-review. Block for
              unlawful content (restricts access).
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant={view === 'listings' ? 'contained' : 'outlined'}
              onClick={() => setView('listings')}
            >
              Listings
            </Button>
            <Button
              variant={view === 'reports' ? 'contained' : 'outlined'}
              startIcon={<ReportIcon />}
              onClick={() => setView('reports')}
            >
              Reports ({reports.length})
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={() => { loadRows(); loadReports(); }} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>How Locsy protects you:</strong> Marketplace groups need admin approval (like OLX
          + local review). Soft keyword filter blocks obvious illegal terms at publish. You can
          Pause (stop showing) / Resume, or Block for policy violations. Locsy is an intermediary —
          users are responsible for their listings.
        </Alert>

        {info && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
            {info}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {view === 'reports' ? (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>Listing</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{r.posting_title || r.posting_id}</Typography>
                        <Typography variant="caption">{r.category_key}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.reason} color="warning" />
                      </TableCell>
                      <TableCell>{r.details || '—'}</TableCell>
                      <TableCell>{r.posting_status}</TableCell>
                      <TableCell>
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {reports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        No open reports.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              {STATUS_TABS.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  label={
                    t.key === 'all'
                      ? t.label
                      : `${t.label}${tab === t.key || t.key === 'pending' ? ` (${counts[t.key] ?? '…'})` : ''}`
                  }
                />
              ))}
            </Tabs>

            <TextField
              size="small"
              placeholder="Search title, name, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2, minWidth: 320 }}
            />

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Visibility</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{row.title}</Typography>
                          {row.budget_label && (
                            <Typography variant="caption" color="text.secondary">
                              {row.budget_label}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.category_label || row.category_key}</TableCell>
                        <TableCell>
                          {row.user_name || '—'}
                          <br />
                          <Typography variant="caption">{row.user_phone || 'Phone hidden'}</Typography>
                        </TableCell>
                        <TableCell>{row.location_name || '—'}</TableCell>
                        <TableCell>
                          {row.visible_to_citizens ? (
                            <Chip size="small" label="Public" color="success" sx={{ mr: 0.5 }} />
                          ) : (
                            <Chip size="small" label="Hidden" sx={{ mr: 0.5 }} />
                          )}
                          {row.visible_to_providers && (
                            <Chip size="small" label="Providers" color="secondary" />
                          )}
                        </TableCell>
                        <TableCell>{statusChip(row.status, row.is_paused)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => { setSelected(row); setNotes(''); }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          No postings in this tab.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Moderate listing</DialogTitle>
          <DialogContent>
            {selected && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Typography variant="h6">{selected.title}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selected.description}
                </Typography>
                <Typography variant="body2">
                  <strong>Category:</strong> {selected.category_label || selected.category_key}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone shown:</strong> {selected.show_phone ? 'Yes' : 'No (in-app only)'}
                </Typography>
                {statusChip(selected.status, selected.is_paused)}
                <TextField
                  label="Admin notes"
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
            <Button onClick={() => setSelected(null)}>Close</Button>
            {selected?.status === 'approved' && !selected?.is_paused && (
              <Button
                color="warning"
                startIcon={<PauseIcon />}
                disabled={acting}
                onClick={() => review('pause')}
              >
                Pause / Stop
              </Button>
            )}
            {selected?.status === 'approved' && selected?.is_paused && (
              <Button
                color="success"
                startIcon={<ResumeIcon />}
                disabled={acting}
                onClick={() => review('resume')}
              >
                Resume
              </Button>
            )}
            {selected?.status !== 'blocked' && (
              <Button
                color="error"
                startIcon={<BlockIcon />}
                disabled={acting}
                onClick={() => review('block')}
              >
                Block (unlawful)
              </Button>
            )}
            {selected?.status !== 'rejected' && (
              <Button
                color="inherit"
                startIcon={<RejectIcon />}
                disabled={acting}
                onClick={() => review('reject')}
              >
                Not Approve
              </Button>
            )}
            {selected?.status !== 'approved' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                disabled={acting}
                onClick={() => review('approve')}
              >
                Approve & Go Live
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default UserPostings;
